// plan-13 S05: broadcast generator — deterministic 48-line transmission per UTC date

import { fromString, mulberry32 } from '../../core/seed'
import { pickLines } from './templates'

export interface BroadcastLine {
  at_ms: number
  text: string
  color: 'amber' | 'king-green' | 'term-green' | 'rain' | 'golden'
  emphasis?: 'calm' | 'rising' | 'peak' | 'falling'
}

export interface BroadcastTransmission {
  dateKey: string
  lines: BroadcastLine[]
  duration_ms: number
}

const BROADCAST_DURATION_MS = 288_000 // 4m48s
const LINE_COUNT = 48

export function generateBroadcast(dateKey: string): BroadcastTransmission {
  const seed = fromString(dateKey)
  const rng = mulberry32(seed)

  // pick 48 template lines
  const templates = pickLines(dateKey, rng)

  // distribute across 288 s with deliberate clustering:
  // quiet opening → building middle → peak around line 24 → falling
  const offsets: number[] = []
  for (let i = 0; i < LINE_COUNT; i++) {
    // nonlinear distribution: more space at start and end, dense in middle
    const t = i / (LINE_COUNT - 1) // 0..1
    // bell-curve-ish mapping: center is dense
    const mapped = 0.5 + (t - 0.5) * (1 - 0.3 * Math.pow(2 * t - 1, 2))
    const baseMs = mapped * BROADCAST_DURATION_MS
    // add small jitter
    const jitter = (rng() - 0.5) * 2000 // ±1s
    offsets.push(Math.max(0, Math.min(BROADCAST_DURATION_MS - 100, baseMs + jitter)))
  }

  // sort offsets chronologically
  offsets.sort((a, b) => a - b)

  const lines: BroadcastLine[] = templates.map((tmpl, i) => ({
    at_ms: Math.round(offsets[i]),
    text: tmpl.text,
    color: tmpl.color,
    emphasis: tmpl.emphasis,
  }))

  return {
    dateKey,
    lines,
    duration_ms: BROADCAST_DURATION_MS,
  }
}
